import React, { useState, useEffect } from 'react';
import { Eye, Map } from 'lucide-react';
import FormModal from './FormModal';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Process Map Viewer Component using React Flow
const ProcessMapViewer: React.FC<{ diagramData: string }> = ({ diagramData }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const data = JSON.parse(diagramData);
      console.log('ProcessMapViewer data:', data);

      if (data.nodes && Array.isArray(data.nodes) && data.edges && Array.isArray(data.edges)) {
        // Transform nodes to React Flow format
        const flowNodes: Node[] = data.nodes.map((node: any, index: number) => {
          // Calculate position with default spacing if not provided
          const x = node.x !== undefined ? node.x : (index % 3) * 200 + 50;
          const y = node.y !== undefined ? node.y : Math.floor(index / 3) * 100 + 50;

          return {
            id: String(node.id || index),
            type: node.type || 'process',
            position: { x, y },
            data: { label: node.label || node.name || `Node ${index + 1}` },
            style: {
              width: 150,
              height: 60,
            },
          };
        });

        // Transform edges to React Flow format
        const flowEdges: Edge[] = data.edges.map((edge: any, index: number) => ({
          id: `edge-${index}`,
          source: String(edge.from || edge.source || index),
          target: String(edge.to || edge.target || index + 1),
          label: edge.label || edge.name || '',
          type: 'smoothstep',
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { strokeWidth: 2, stroke: '#374151' },
        }));

        setNodes(flowNodes);
        setEdges(flowEdges);
        setError(null);
      } else {
        setError('Invalid diagram data format');
      }
    } catch (err) {
      console.error('Error parsing diagram data:', err);
      setError('Failed to parse diagram data');
    }
  }, [diagramData]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="h-96 border border-slate-200 rounded-lg overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        attributionPosition="bottom-left"
      >
        <Background variant={BackgroundVariant.Dots} />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
};

interface ProcessMap {
  id: number;
  title: string;
  description: string;
  diagram_data: string;
  category: string;
  framework_id?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ProcessMapPreviewModalProps {
  onClose: () => void;
  processMap: ProcessMap;
}

export const ProcessMapPreviewModal: React.FC<ProcessMapPreviewModalProps> = ({
  onClose,
  processMap,
}) => {
  return (
    <FormModal
      title={`Preview: ${processMap.title}`}
      subtitle="See how your process map will appear"
      icon={<Eye className="text-white" size={24} />}
      onSubmit={() => {}} // No submit action for preview
      onCancel={onClose}
      cancelText="Close"
      showSubmitButton={false}
      size="large"
    >
      <div className="space-y-6">
        <div className="bg-slate-50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Map className="text-blue-600" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{processMap.title}</h3>
              <p className="text-slate-600 mb-4">{processMap.description}</p>

              <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                <span className="flex items-center gap-1">
                  <span className="font-medium">Category:</span> {processMap.category}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  processMap.is_active
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-700'
                }`}>
                  {processMap.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Process Map Diagram</h4>
          {processMap.diagram_data ? (
            <ProcessMapViewer diagramData={processMap.diagram_data} />
          ) : (
            <div className="bg-slate-100 rounded-lg p-8 text-center">
              <Map className="mx-auto text-slate-400 mb-2" size={48} />
              <p className="text-slate-600">No diagram data available</p>
            </div>
          )}
        </div>
      </div>
    </FormModal>
  );
};